import { animated, easings, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { useTouchScreen } from '#src/hooks/device/useTouchScreen.ts';
import { useIsVisibleInViewport } from '#src/hooks/ui/useIsVisibleInViewport.ts';
import { mergeRefs } from '#src/lib/mergeRefs.ts';

const IDLE_DURATION_MS = 2000;
const IDLE_RESTART_DELAY_MS = 1000;

const Root = styled.div`
  position: relative;
  height: 100%;
`;

const SpringLayer = styled(animated.div)`
  height: 100%;
`;

type Props = {
  idle?: boolean;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
};

export const InteractiveElement = ({
  idle: idleProp = false,
  disabled = false,
  className,
  children,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const isTouchScreen = useTouchScreen();
  const idle = isTouchScreen || idleProp;
  const disableEffect = disabled || (isTouchScreen && !idleProp);
  const restartTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const { handleRef: viewportRef, isVisible } =
    useIsVisibleInViewport<HTMLDivElement>();
  const visibleOrUnobserved = isVisible ?? true;
  const springLayerRef = useMemo(
    () => mergeRefs<HTMLDivElement | null>(ref, viewportRef),
    [viewportRef]
  );

  const [styles, api] = useSpring(() => ({
    from: { '--mx': 0.5, '--my': 0.5, '--active': 0 },
    config: { mass: 5, tension: 350, friction: 40 },
  }));

  const startIdleLoop = useCallback(() => {
    if (!visibleOrUnobserved) return;

    api.start({
      from: { '--mx': -0.1, '--my': -0.1, '--active': 1 },
      to: { '--mx': 1.1, '--my': 1.1, '--active': 1 },
      loop: true,
      config: {
        duration: IDLE_DURATION_MS,
        easing: easings.easeInOutQuint,
      },
      delay: 2000,
    });
  }, [api, visibleOrUnobserved]);

  useEffect(() => {
    if (!idle || disableEffect) return () => {};
    startIdleLoop();
    return () => {
      api.stop();
      clearTimeout(restartTimerRef.current);
    };
  }, [idle, disableEffect, api, startIdleLoop]);

  const dragBind = useDrag(
    ({ xy: [px, py], first, last }) => {
      if (disabled) return;
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;

      if (first) {
        clearTimeout(restartTimerRef.current);
        api.stop();
      }

      const mx = Math.min(1, Math.max(0, (px - rect.left) / rect.width));
      const my = Math.min(1, Math.max(0, (py - rect.top) / rect.height));
      api.start({
        '--mx': mx,
        '--my': my,
        '--active': 1,
        config: {
          mass: 1,
          tension: 600,
          friction: 30,
          duration: undefined,
          easing: undefined,
        },
      });

      if (last) {
        api.start({
          '--active': 0,
          config: {
            mass: 5,
            tension: 350,
            friction: 40,
            duration: undefined,
            easing: undefined,
          },
        });
        restartTimerRef.current = setTimeout(
          startIdleLoop,
          IDLE_RESTART_DELAY_MS
        );
      }
    },
    { pointer: { touch: true } }
  );

  const springStyle = {
    '--mx': styles['--mx'].to(v => v),
    '--my': styles['--my'].to(v => v),
    '--active': styles['--active'].to(v => v),
  } as CSSProperties;

  return (
    <Root className={classNames(className)}>
      {disableEffect ? (
        children
      ) : isTouchScreen ? (
        <SpringLayer ref={springLayerRef} {...dragBind()} style={springStyle}>
          {children}
        </SpringLayer>
      ) : (
        <SpringLayer
          ref={springLayerRef}
          onMouseMove={({ clientX: mx, clientY: my }) => {
            api.stop();
            const rect = ref?.current?.getBoundingClientRect();
            if (rect) {
              api.start({
                '--mx': (mx - rect.left) / rect.width,
                '--my': (my - rect.top) / rect.height,
                '--active': 1,
                config: { mass: 1, tension: 6000, friction: 100 },
              });
            }
          }}
          onMouseLeave={() => {
            api.start({ '--active': 0 });
            if (idle) {
              startIdleLoop();
            }
          }}
          style={springStyle}
        >
          {children}
        </SpringLayer>
      )}
    </Root>
  );
};
